import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import SubmitButton from '../components/SubmitButton';
import 'react-toastify/dist/ReactToastify.css';
import FormHeader from '../components/FormHeader';
import FormVarTH from '../components/FormVarTH';
import FormMoney from '../components/FormMoney';
import useSubmitPost from '../hooks/handleSubmitPost';
import FormVarF from '../components/FormVarF';
import FormCity from '../components/FormCity';
import FormState from '../components/FormState';
import FormZip from '../components/FormZip';
import FormVarH from '../components/FormVarH';
import FormText from '../components/FormText';
import FormVarTF from '../components/FormVarTF';
import FormPhone from '../components/FormPhone';
import SelectCustomerMaintenance from '../components/SelectCustomerMaintenance';

const MaintenanceContractNew = () => {
  const navigate = useNavigate();
  const { key, id } = useParams();
  const handleSubmit = useSubmitPost();
  const [errors, setErrors] = useState({});
  const [jobSite, setJobSite] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  
  // Updated contract state to include all fields from the updated table
  const [contract, setContract] = useState({
    id: '',
    customer_id: '',
    start_year: '',
    contract_type: '',
    job_site_address: '',
    job_site_address2: '',
    city: '',
    state: '',
    zip_code: '',
    frequency: '',
    hours: '',
    notes: '',
    system_type: '',
    current_price: '',
    exp_date_contract: '',
    auto_renew: 1,
    status: 1,
    terms_begin: '',
    signed_date: '',
    file: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    last_inspection_date: '',
    due_dates_shifted: 0,
    renewal_rate: '',
    contract_duration: ''
  });

  const apiUrl = process.env.REACT_APP_API_URL;
  const api = `${apiUrl}/contracts_crud.php`;

  useEffect(() => {
    const fetchContract = async () => {
      if (key !== 'new') {
        try {
          const response = await axios.get(api, {
            params: { getContractDetail: true, contract_id: id },
          });
          const data = response.data;
          if (data === 'error') {
            toast.error(data.error, {
              position: 'top-center',
            });
          } else {
            setContract(data || {});
          }
        } catch (error) {
          if (error.response && error.response.status === 401) {
            toast.error('Session expired. Please login again.', {
              position: 'top-center',
            });
            navigate('/login');
          } else if (error.response && error.response.status === 403) {
            navigate('/contracts_list');
            toast.error("You don't have access to this area.", {
              position: 'top-center',
            });
          }
        }
      } 
      else if (key !== 'new' && id !== 'new')
      {
        try {
          const response = await axios.get(api, {
            params: {
              getNewContract: true,
              customer_id: id,
            },
          });
          const data = response.data;
          if (data.error) {
            toast.error(data.error, {
              position: 'top-center',
            });
            navigate('/contracts_list');
          } else {
            setContract(data.contract_detail || {});
          }
        } catch (error) {
          console.warn('Error fetching data', error);
        }
      }
    };

    fetchContract();
  }, [key, id, api, navigate]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    let newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;

    // Copy of current errors
    let newErrors = { ...errors };

    if (name === 'hours') {
      // This regex allows partial decimals like '26.' or '26.5'
      const partialDecimalPattern = /^\d*\.?\d{0,2}$/;
      if (partialDecimalPattern.test(value)) {
        newValue = value; // keep as string if partial
      } else {
        // optionally show an error or ignore the input
      }
    }

    // Validate Email
    if (name === 'contact_email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (newValue && !emailRegex.test(newValue)) {
        newErrors.contact_email = 'Invalid email address.';
      } else {
        delete newErrors.contact_email;
      }
    }

    if (name === 'contact_phone') {
      const phoneRegex = /^[0-9]{0,10}$/; // allows 0 to 10 digits only
      if (newValue && !phoneRegex.test(newValue)) {
        newErrors.contact_phone = 'Invalid phone number. Use up to 10 digits only.';
      } else {
        delete newErrors.contact_phone;
      }
    }

    // Validate Zip Code (US format)
    if (name === 'job_site_zip') {
      const zipRegex = /^[0-9]{5}(?:-[0-9]{4})?$/; 
      if (newValue && !zipRegex.test(newValue)) {
        newErrors.job_site_zip = 'Invalid zip code (use 5 digits or ZIP+4 format).';
      } else {
        delete newErrors.job_site_zip;
      }
    }

    setContract((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    setErrors(newErrors);
  };

  const handleCompanySelect = (newJobSite) => {
    const customer_name = newJobSite?.customer_name || '';
    const customer_id = newJobSite?.id || null;

    if (newJobSite && newJobSite.id) {
      setSelectedCompany(newJobSite);
      setContract((prev) => ({
        ...prev,
        customer_name,
        customer_id
      }));
    } else {
      console.warn('No valid company selected');
    }
  };

  const isSubmitDisabled =
    !contract.customer_id ||
    !contract.job_site_address ||
    !contract.city ||
    !contract.state ||
    !contract.frequency ||
    !contract.current_price ||
    !contract.hours;

  const handleSubmitNewContract = async (e) => {
    e.preventDefault();

  const bodyData = {
    status: 'submit',
    key: key,
    data: {
      ...contract,
    },
  };
    try {
      const response = await axios.post(`${apiUrl}/contracts_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.data.success) {
        toast.success('Successfully saved!', {
          position: 'top-center',
        });
        navigate(`/maintenance`);
      } else {
        toast.error(response.data.error || 'Failed to save the contract.', {
          position: 'top-center',
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error('You do not have permission to edit this service call.', {
          position: 'top-center',
        });
      }
    }
  };    

  return (
    <>
      <div className="flex items-center justify-center min-h-screen bg-gray-100 py-5">
        <form onSubmit={handleSubmitNewContract} className="w-[800px] bg-white border rounded-lg py-8 px-8">
          <div className="space-y-12">
            <FormHeader type={key === 'edit' ? 'Edit' : 'New'} what={'Maintenance Contract'} />

            <div className="border-b border-gray-900/10 pb-12">
              <h6 className="text-base font-semibold leading-7 text-gray-600">
                {key !== 'new' && (
                  <>
                    <span className="text-gray-700">
                      Contract ID: {id}
                    </span>
                  </>
                )}
              </h6>
              <br />

              <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6"> 
                
                {/* Auto Renew (Checkbox) */}
                <div className="col-span-full flex items-center">
                  <input
                    type="checkbox"
                    id="auto_renew"
                    name="auto_renew"
                    checked={contract.auto_renew === 1}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                  <label htmlFor="auto_renew" className="ml-2 block text-sm font-medium text-gray-900">
                    Auto Renew
                  </label>
                </div>      

                {/* Signed Date */}
                <div className="sm:col-span-2">
                  <label htmlFor="signed_date" className="block text-sm font-medium text-gray-900 py-1">
                    Signed Date
                  </label>
                  <input
                    type="date"
                    id="signed_date"
                    name="signed_date"
                    value={contract.signed_date || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm 
                              focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                {/* Expiration Date Contract */}
                {contract.auto_renew !== 1 && (
                  <div className="sm:col-span-2">
                    <label htmlFor="exp_date_contract" className="block text-sm font-medium text-gray-900 py-1">
                      Expiration Date
                    </label>
                    <input
                      type="date"
                      id="exp_date_contract"
                      name="exp_date_contract"
                      value={contract.exp_date_contract || ''}
                      onChange={handleChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm 
                                focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                )}

                {/* Start Year */}
                <FormVarTF 
                  label={'Start Year'} 
                  inside={'start_year'} 
                  val={contract.start_year} 
                  onChange={handleChange} 
                />

                {/* renewal rate */}
                <div className="sm:col-span-1">
                  <label htmlFor={'renewal_rate'} className="block text-sm font-medium leading-6 text-gray-900">
                    Renewal Rate
                  </label>
                  <div className="mt-2">
                    <input
                      type="number"
                      name={'renewal_rate'}
                      id={'renewal_rate'}
                      maxLength="4"
                      value={contract.renewal_rate || ''}
                      onChange={handleChange} 
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>                

                {/* Contract Type */}
                <div className="sm:col-span-2">
                  <label htmlFor="contract_type" className="block text-sm font-medium text-gray-900 py-1">
                    Contract Type
                  </label>
                  <select
                    id="contract_type"
                    name="contract_type"
                    value={contract.contract_type || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                  >
                    <option value="">Select Contract Type</option>
                    <option value="Base Building">Base Building</option>
                    <option value="Tenant">Tenant</option>
                    <option value="Sub-System">Sub-System</option>
                    <option value="Pre-Action">Pre Action</option>
                  </select>
                </div>                

                {/* Select Job Site */}
                {contract.customer_name ? (
                  <div className="col-span-full">
                  <label htmlFor="customer_name" className="block text-sm font-medium text-gray-900">
                    Customer Name <span className="text-red-500">*</span> | Note: by clearing the job site field (activates the search engine)
                  </label>
                  <input
                    type="text"
                    id="customer_name"
                    name="customer_name"
                    value={contract.customer_name || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                  />
                </div>
                ) : (
                  <div className="sm:col-span-3">
                    <SelectCustomerMaintenance
                      label="Select Customer Name"
                      items={jobSite}
                      selectedItem={selectedCompany}
                      setSelectedItem={handleCompanySelect}
                      apiUrl={`${apiUrl}/contracts_crud.php`}
                    />
                  </div>
                )}

                {/* Job Site Address */}
                <FormVarH
                  label={'Job Site Address'}
                  inside={'job_site_address'}
                  val={contract.job_site_address}
                  onChange={handleChange} />

                {/* Job Site Address2 */}
                <FormVarH
                  label={'Address2'}
                  inside={'job_site_address2'}
                  val={contract.job_site_address2}
                  onChange={handleChange}
                  required={false}
                />

                {/* Job Site City */}
                <FormCity
                  val={contract.city}
                  onChange={handleChange}
                />

                {/* Job Site State */}
                <FormState
                  val={contract.state}
                  onChange={handleChange}
                />

                {/* Job Site Zip */}
                <FormZip
                  val={contract.zip_code}
                  onChange={handleChange}
                  type="number"
                  required={false}
                />

                {/* System Type */}
                <FormVarF
                  label={'System Type'}
                  inside={'system_type'}
                  val={contract.system_type}
                  onChange={handleChange}
                />

                {/* Frequency */}
                <div className="sm:col-span-2">
                  <label htmlFor="frequency" className="block text-sm font-medium text-gray-900 py-1">
                    Frequency
                  </label>
                  <select
                    id="frequency"
                    name="frequency"
                    value={contract.frequency || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                  >
                    <option value="">Select Frequency</option>
                    <option value="12">Annually</option>
                    <option value="6">Semi-Annually</option>
                    <option value="3">Quarterly</option>
                    <option value="1">Monthly</option>
                  </select>
                </div>

                {/* Hours */}
                <div className="sm:col-span-1">
                  <label htmlFor='hours' className="block text-sm font-medium leading-6 text-gray-900">
                    Hours  <span className='text-red-500'>*</span> 
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="hours"
                      id="hours"
                      pattern="^\d+(\.\d{1,2})?$"
                      // maxLength="4"
                      value={contract.hours || ''} 
                      onChange={handleChange}
                      step="any"
                      required={true}     
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                {/* Duration */}
                <div className="sm:col-span-1">
                  <label htmlFor={'contract_duration'} className="block text-sm font-medium leading-6 text-gray-900">
                    Duration
                  </label>
                  <div className="mt-2">
                    <input
                      type="number"
                      name={'contract_duration'}
                      id={'contract_duration'}
                      maxLength="4"
                      value={contract.contract_duration || ''}
                      onChange={handleChange} 
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>                            

                {/* Notes */}
                <FormText 
                  label={'Notes'} 
                  inside={'notes'} 
                  val={contract.notes} 
                  onChange={handleChange} 
                />

                {/* Current Price */}
                <FormMoney
                  inside={'current_price'}
                  val={contract.current_price}
                  onChange={handleChange}
                  required={false}
                />

                {/* Terms Begin (Months select) */}
                <div className="sm:col-span-2">
                  <label htmlFor="terms_begin" className="block text-sm font-medium text-gray-900 py-1">
                    Terms Begin (Month)
                  </label>
                  <select
                    id="terms_begin"
                    name="terms_begin"
                    value={contract.terms_begin || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="">Select Month</option>
                    <option value="January">January</option>
                    <option value="February">February</option>
                    <option value="March">March</option>
                    <option value="April">April</option>
                    <option value="May">May</option>
                    <option value="June">June</option>
                    <option value="July">July</option>
                    <option value="August">August</option>
                    <option value="September">September</option>
                    <option value="October">October</option>
                    <option value="November">November</option>
                    <option value="December">December</option>
                  </select>
                </div>

                {/* Contact Name */}
                <FormVarH
                  label={'Contact Name'}
                  inside={'contact_name'}
                  required={false}
                  val={contract.contact_name}
                  onChange={handleChange}/>

                {/* Contact Phone */}
                <FormPhone
                label={'Contact Phone'}
                  inside={'contact_phone'}
                  val={contract.contact_phone}
                  required={false}
                  onChange={handleChange}/>
                  {errors.contact_phone && (
                      <h6 className='text-xs text-red-500'>{errors.contact_phone} </h6>
                  )}

                {/* Contact Email */}
                <FormVarH
                  label={'Contact Email'}
                  inside={'contact_email'}
                  val={contract.contact_email}
                  onChange={handleChange}
                  required={false}/>
                  {errors.contact_email && (
                      <h6 className='text-xs text-red-500'>{errors.contact_email}</h6>
                  )}

                {/* Last Inspection Date */}
                <div className="sm:col-span-2">
                  <label htmlFor="last_inspection_date" className="block text-sm font-medium text-gray-900 py-1">
                    Last Inspection /FDNY Test Date
                  </label>
                  <input
                    type="date"
                    id="last_inspection_date"
                    name="last_inspection_date"
                    value={contract.last_inspection_date || ''}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm 
                              focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-x-6">
            <NavLink to={`/maintenance`}>
              <button type="button" className="text-sm font-semibold leading-6 text-gray-900">
                Contracts List
              </button>
            </NavLink>
            <SubmitButton disabled={isSubmitDisabled}>Submit</SubmitButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default MaintenanceContractNew;
